
import {Component, Vue, Watch} from "vue-property-decorator";
import KeyDetail from "@/components/keys/KeyDetail.vue";
import KeySelected from "@/components/keys/KeySelected.vue";
import KeyStatus from "@/components/keys/KeyStatus.vue";
import KeyGroupCreation from "@/components/key-groups/KeyGroupCreation.vue";
import ImportModal from "@/components/import/ImportModal.vue";
import ImportResults from "@/components/import/ImportResults.vue";
import {Getter} from "vuex-class";
import KeyCreate from "@/components/keys/KeyCreate.vue";
import KeyGroups from "@/views/front-office/KeyGroups.vue";
import ListKeyItem from "@/components/list/ListKeyItem.vue";
import RelayBookedKey from "@/components/relays/RelayBookedKey.vue";
import SimpleDialog from "@/components/SimpleDialog.vue";
import CustomChipLink from "@/components/CustomChipLink.vue";
import CardKey from "@/components/cardKey.vue";
import { CONTEXT_TYPES, IContext } from "@/store/modules/context";
import RelayGiveKeyToGuest from "@/components/relays/RelayGiveKeyToGuest.vue";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiExport, mdiFocusFieldHorizontal, mdiBell, mdiBellOffOutline } from "@mdi/js";
import KeyAddManagersDialog from "@/components/keys/managers/KeyAddManagersDialog.vue";
import KeyAddGuestDialog from "@/components/keys/guests/KeyAddGuestDialog.vue";
import { ROWS_PER_PAGE } from "@/utils/constants";
import { getTokenLink } from "@/utils/misc";

@Component({
    computed: {
      mdiExport: () => mdiExport,
    },
    components: {
        KeyAddGuestDialog,
        KeyAddManagersDialog,
        RelayGiveKeyToGuest,
        CardKey,
        KeyCreate,
        KeySelected,
        KeyStatus,
        KeyGroupCreation,
        KeyDetail,
        ImportModal,
        ImportResults,
        KeyGroups,
        ListKeyItem,
        RelayBookedKey,
        SimpleDialog,
        CustomChipLink,
        SvgIcon,
    },
})
export default class Keys extends Vue {

  get lang(): string {
    return this.$root.$i18n.locale;
  }

  get isCompanyBlocked(): boolean {
    return !!this.currentUser?.company?.blockedAt;
  }

  get canCreateKey(): boolean {
    return !this.isCompanyBlocked;
  }

  get displayArchivedKeysToggle(): boolean {
    return this.pagination.tab === "myKeys";
  }

  private get canArchiveAllSelectedKeys(): boolean {
    return this.selectedKeys?.every((key: any) => Keys.isKeyStatusArchivable(key));
  }

  private static isKeyStatusArchivable(key: any): boolean {
    const unauthorizedStatus: string[] = ["ARCHIVED", "IN_RELAY", "IN_TRANSIT"];
    return !unauthorizedStatus.includes(key.status);
  }

  @Getter private currentUser: any;
  @Getter private isDemo: boolean;
  @Getter private canDoInEnvCompany: (right: string) => boolean;
  @Getter private context: IContext|null;
  @Getter private envCompanyId: number|null;
  @Getter private isBookedKeyAvailable: (key: any) => boolean;

  private keys: any[] = [];
  private headers: object[] = [];
  private currentKeyId: number = null;
  private uploadedImageSrc: any = null;
  private statsDialog: boolean = false;
  private displayCreateKeyGroupDialog: boolean = false;
  private archiveAllDialog: boolean = false;
  private loadingArchived: boolean = false;
  private quickAddGuestDialog: boolean = false;
  private quickAddManagerDialog: boolean = false;
  private quickBookedKeyDialog: boolean = false;
  private loading: boolean = false;
  private loadingAddGuest: boolean = false;
  private keysSearch: string = null;
  private stats: any = null;
  private pagination: any = {
    sortDesc: [true],
    sortBy: ["updatedAt"],
    search: "",
    statuses: [],
    categories: [],
    page: 1,
    itemsPerPage: 10,
    tab: "myKeys",
  };
  private totalKeys: number = 0;
  private selectedKeys: any[] = [];
  private keysStatus: string[] = [];
  private displayKeysFilter: boolean = false;
  private displayCategoryFilter: boolean = false;
  private displayRelaysFilter: boolean = false;
  private showCreateDialog: boolean = false;
  private showArchivedKeys: boolean = false;
  private tabHasChanged: boolean = false;
  private categories: any[] = [];
  private preference: any = {};
  private mosaicView: boolean = false;
  private incrementPagination: number = 0;

  private mdiFocusFieldHorizontal = mdiFocusFieldHorizontal;
  private mdiBell = mdiBell;
  private mdiBellOffOutline = mdiBellOffOutline;
  // Export all keys
  private exportAllDialog: boolean = false;
  private loadingExportAllKeys: boolean = false;
  private ROWS_PER_PAGE = ROWS_PER_PAGE;

  @Watch("pagination.tab")
  public handlerPaginationTab(val, oldVal) {
    if (val !== "groups") {
      if (val !== oldVal) {
        this.tabHasChanged = true;
        if (this.keysSearch !== null) {
          this.keysSearch = null;
        }
        this.pagination = {
          sortDesc: [true],
          sortBy: ["updatedAt"],
          search: "",
          categories: [],
          statuses: [],
          page: 1,
          itemsPerPage: 10,
          tab: val,
        };
      }
      this.reset();
    }
  }

  @Watch("pagination.page")
  public handlerPaginationPage(val, oldVal) {
    this.handlerPaginationChanged(val, oldVal);
  }

  @Watch("pagination.itemsPerPage")
  public handlerPaginationItemsPerPage(val, oldVal) {
    this.handlerPaginationChanged(val, oldVal);
  }

  @Watch("pagination.search")
  public handlerPaginationSearch(val, oldVal) {
    this.handlerPaginationChanged(val, oldVal);
  }

  @Watch("pagination.statuses")
  public handlerPaginationSelect(val, oldVal) {
    this.handlerPaginationChanged(JSON.stringify(val), JSON.stringify(oldVal));
  }

  @Watch("pagination.categories")
  public handlerPaginationCategory(val, oldVal) {
    this.handlerPaginationChanged(JSON.stringify(val), JSON.stringify(oldVal));
  }

  @Watch("pagination.sortBy")
  public handlerPaginationSortBy(val, oldVal) {
    this.handlerPaginationChanged(val, oldVal);
  }

  @Watch("pagination.sortDesc")
  public handlerPaginationSortDesc(val, oldVal) {
    this.handlerPaginationChanged(val, oldVal);
  }

  @Watch("mosaicView")
  public mosaicViewHandler(val) {
    this.pagination.itemsPerPage = val ? 25 : 10;
  }

  @Watch("preference", {deep: true})
  public handlerPreferenceRelays(val, oldVal) {
    if (!this.currentUser.preference?.relays) {
      val.relays.forEach((r) => {
        r.status = true;
      });
    }
    const isSame = val.relays.every((relay, index) => {
      return relay && oldVal.relays[index] && relay.status === oldVal.relays[index].status;
    });
    this.$store.dispatch("users/savePreference", {
      id: this.currentUser.id,
      preference: val,
    });
    this.currentUser.preference = val;
    this.reset();
  }

  @Watch("envCompanyId", { immediate: true })
  public async handlerEnvCompanyId() {
    // Fetch total keys for the current company
    if (this.envCompanyId) {
      await this.reset();
      this.loading = false;
    }
  }

  private handlerPaginationChanged(val, oldVal): void {
    if (val !== oldVal && this.tabHasChanged === false) {
      this.reset();
    }
  }

  private updateShowArchivedKey(): void {
    this.reset();
  }

  private enterKeyPress(e) {
    if (e.keyCode === 13) {
      this.searchHandler();
    }
  }

  private clearContext() {
    this.$store.dispatch("context/clearContext");
    this.reset();
  }

  private searchHandler(): void {
    this.pagination.search = this.keysSearch || null;
    this.pagination.page = 1;
  }

  private mounted() {
    this.headers = [
      {value: "name", sortable: true},
      {value: "Relays", sortable: false},
      {value: "status", sortable: true},
      {value: "category", sortable: false},
      {value: "location", sortable: false},
      {value: "action", sortable: false},
      {value: "followed", sortable: false},
    ];
    this.statsDialog = !!this.$route.params.data;
    this.stats = this.$route.params.data;
    this.getCategories();
    this.getKeysStatus();
    this.getPreference();
    if (this.$route.params.tab && !this.isDemo) {
      this.pagination.tab = this.$route.params.tab;
    }
    this.scroll();
  }

  private changeTabs(tab) {
      if (!this.isDemo && tab !== this.pagination?.tab) {
          this.pagination.tab = tab;
          this.selectedKeys = [];
          this.keys = [];
          this.showArchivedKeys = false;
      }
  }

  private async reset(): Promise<void> {
      this.incrementPagination = 0;
      this.loading = true;
      this.loadingAddGuest = false;
      this.uploadedImageSrc = null;
      this.currentKeyId = null;
      await this.getKeys();
      this.loading = false;
  }

  private getCategories(): void {
    this.$store.dispatch("categories/getAll")
      .then((res) => {
        this.categories = res.categories;
      });
  }

  private async getPreference(): Promise<void> {
    if (this.envCompanyId) {
      await this.$store.dispatch("companies/getRelaysFilterList", {
        companyId: this.envCompanyId,
      }).then((res: any[]) => {
        if (res?.length) {
          const preferenceRelays: any[] = res.map((relay) => ({
            relayId: relay.id,
            relayName: relay.name,
            status: this.currentUser.preference?.relays
              ?.find((relayPref) => relayPref.relayId === relay.id)
              ?.status === true || false,
          }));
          this.$set(this.preference, "relays", preferenceRelays);
        }
      });
    }
  }

  private fetchKeys(customParams?): any {
    if (!this.envCompanyId) {
      throw new Error("MISSING_ENV_COMPANY_ID");
    }
    const params = customParams || this.pagination;
    let relaysIds = null;
    if (this.pagination?.tab === "myKeys" && this.preference.relays?.length > 1) {
      relaysIds = this.preference.relays
        .filter((pref) => pref.status)
        .map((pref) => pref.relayId);
    }
    const filters = {
      role: this.pagination?.tab === "myKeys" ? "MANAGER" : "GUEST",
      companyId: this.envCompanyId,
      search: params.search,
      statuses: params.statuses,
      archived: this.showArchivedKeys,
      boxId: this.context?.type === CONTEXT_TYPES.BOX ? this.context.id : null,
      categoriesIds: params.categories,
      relaysIds,
    };
    const pagination = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
      sortBy: params.sortBy,
      sortDesc: params.sortDesc,
    };
    const p: Array<Promise<any>> = [
      this.$store.dispatch("users/getMyKeys", {
        id: this.currentUser.id,
        query: {
          ...filters,
          ...pagination,
        },
      }),
    ];
    return Promise.all(p).then(([classicKeys, lateKeys]) => {
      // Merge late keys and classic keys, with late keys always at the top of the table
      const keys = [
        ...lateKeys?.keys || [],
        ...classicKeys?.keys || [],
      ];
      const count = (classicKeys?.count || 0) + (lateKeys?.count || 0);
      return {keys, count};
    });
  }

  private formattedKey = (key: any) => ({
    ...key,
    isFollowed: key.isFollowed || false,
    relayId: key?.Relays?.find((r) => r.type === "AGENCY")?.id,
    // Display either current location or reserved location if the key is not in relay
    locationDisplayed: key.currentLocation || key.ReservedLocations?.[0],
    // Display either current relay or main relay of the key (agency for b2b, first else)
    relayDisplayed: key?.currentLocation?.Box?.Building?.Relays?.[0]
      || key.Relays?.find((relay) => relay.type === "AGENCY")
      || key.Relays?.[0],
  })

  private getKeys(): Promise<any> {
    return this.fetchKeys().then((result) => {
      this.selectedKeys = [];
      this.keys = result.keys.map((key) => this.formattedKey(key));
      this.totalKeys = result?.count || 0;
      this.close();
      this.tabHasChanged = false;
    }).catch((error) => {
      this.$store.commit("alerts/displayHttpError", { error });
    }).finally(() => {
      this.loading = false;
    });
  }

  private clickRow(item) {
    return this.$router.push({name: "key", params: {id: item.id}});
  }

  private updateKeyRow(keyId: number, newKeyData: any): void {
    const keyIdx: number = this.keys.findIndex((k) => k.id === keyId);
    const actualKeyData = this.keys[keyIdx];
    const keys = [...this.keys];
    keys[keyIdx] = {
      ...actualKeyData,
      ...newKeyData,
    };
    this.keys = keys;
  }

  private fetchKeyDetails(keyId: number): Promise<void> {
    this.updateKeyRow(keyId, { loading: true });
    return this.$store.dispatch("keys/getById", {
      id: keyId,
      options: {
        relays: true,
        reservedLocation: true,
        company: true,
        holder: true,
        currentLocation: true,
      },
    }).then(async (key) => {
      // getById doesn't fetch the key in a user specific context, getMyKeys does
      // We take the data from there
      const previousKeyData = this.keys.find((k) => k.id === keyId) || {};
      // Update properties checked by actions components front-end (ISO page /keys/:id)
      key = this.formattedKey({
        ...key,
        isFollowed: previousKeyData.isFollowed,
      });
      // Replace key in current array by key with full data
      key.detailsLoaded = true;
      this.updateKeyRow(keyId, key);
    }).finally(() => {
      this.updateKeyRow(keyId, { loading: false });
    });
  }

  private displayCodeName(key) {
    if (key.backupId) {
      let codeName;
      try {
        const s = key.locationDisplayed.codeName.split("-");
        codeName = s[s.length - 1];
      } catch {
        codeName = "";
      }
      return codeName;
    } else {
      return key.locationDisplayed.codeName;
    }
  }

  private displayKeyLocation(key) {
    return key.locationDisplayed.Box.name;
  }

  private resetKeyGroupCreation() {
      this.close();
      this.selectedKeys = [];
      this.pagination.tab = "groups";
      this.$store.commit("alerts/displaySuccess", {
          msg: this.$i18n?.t("actions.created"),
      });
  }

  private archiveSelectedKeys() {
    this.loadingArchived = true;
    Promise.all(this.selectedKeys.map((key: any) => this.$store
      .dispatch("keys/archive", {keyId: key.id}),
    ))
      .then(async (res) => {
        this.$store.commit("alerts/displaySuccess", {
          msg: this.$i18n?.tc("actions.archived", this.selectedKeys?.length),
        });
        await this.reset();
      })
      .catch((err) => {
        this.$store.commit("alerts/displayError", {
          msg: err,
        });
      })
      .finally(() => this.loadingArchived = false);
  }

  private exportAllKeys(): void {
    const url = getTokenLink(`api/companies/${this.envCompanyId}/export-keys`);
    window.open(url, "_blank");
    this.$store.commit("alerts/displaySuccess", {
      msg: this.$i18n.t("keysList.exportAllKeys.success"),
    });
    this.exportAllDialog = false;
  }

  private getKeysStatus() {
    return this.$store.dispatch("keys/getStatus").then((res) => {
      this.keysStatus = res.keysStatus;
    });
  }

  private displayCreateKeyGroupDialogFn() {
    this.close();
    this.displayCreateKeyGroupDialog = true;
  }

  private displayArchiveAllDialog() {
    this.close();
    this.archiveAllDialog = true;
  }

  private async followKeys() {
    this.$store.dispatch(`users/addFollowedKeys`, {
      userId: this.currentUser.id,
      keyIds: this.selectedKeys.map((key: any) => key.id),
    }).then(() => {
      this.selectedKeys.map((key: any) => key.isFollowed = true);
    }).catch((err) => {
        this.$store.commit("alerts/displayError", {
          msg: err,
        },
      );
    });
  }

  private async unfollowKeys() {
    this.$store.dispatch(`users/removeFollowedKeys`, {
      userId: this.currentUser.id,
      keyIds: this.selectedKeys.map((key: any) => key.id),
    }).then(() => {
      this.selectedKeys.map((key: any) => key.isFollowed = false);
    }).catch((err) => {
        this.$store.commit("alerts/displayError", {
          msg: err,
        },
      );
    });
  }

  private deselectAllKeys() {
    this.selectedKeys = [];
  }

  private openQuickAddGuest(item: any) {
    this.close();
    this.currentKeyId = item.id;
    this.quickAddGuestDialog = true;
  }

  private openQuickAddManager(item: any) {
    this.close();
    this.currentKeyId = item.id;
    this.quickAddManagerDialog = true;
  }

  private toggleFollowed(item: any): void {
    if (this.currentUser.followAllKeys || item.archivedAt) {
      return;
    }
    if (item.isFollowed) {
      this.$store.dispatch("keys/removeFollowedKey", {keyId: item.id, userId: this.currentUser.id})
        .then(() => { item.isFollowed = false; })
        .catch((err) => {
        this.$store.commit("alerts/displayError", {
          msg: err,
        });
      });
    } else {
      this.$store.dispatch("keys/addFollowedKey", {keyId: item.id, userId: this.currentUser.id})
        .then(() => { item.isFollowed = true; })
        .catch((err) => {
        this.$store.commit("alerts/displayError", {
          msg: err,
        });
      });
    }
  }

  private isKeyFollowed(item: any): boolean {
    return this.currentUser.followAllKeys || item.isFollowed;
  }

  private close() {
    this.statsDialog = false;
    this.quickAddGuestDialog = false;
    this.quickAddManagerDialog = false;
    this.quickBookedKeyDialog = false;
    this.displayCreateKeyGroupDialog = false;
    this.archiveAllDialog = false;
    this.exportAllDialog = false;
  }

  private renderRefRelay(key) {
    if (key.Relays?.length > 1) {
      const refRelay = key.Relays.filter((relay) => relay.type === "AGENCY");
      return refRelay[0]?.name;
    } else {
      return key.Relays?.[0]?.name;
    }
  }

  private isCompanyKeyBlocked(key: any) {
      return key?.company && !!key.company.blockedAt;
  }

  private scroll(): void {
    window.onscroll = (): void => {
      const listElm = document.querySelector(".scroll-list");
      const isTargetPos = listElm?.clientHeight <= (document.documentElement.scrollTop + window.innerHeight);
      const allKeysFetch = this.totalKeys === this.keys?.length;
      if (isTargetPos && !this.loading && !allKeysFetch) {
        this.loading = true;
        this.incrementPagination++;
        this.fetchKeys({
          ...this.pagination,
          page: this.pagination.page + this.incrementPagination,
        }).then((res) => {
          this.keys = [
            ...this.keys,
            ...res.keys.map((key) => this.formattedKey(key)),
          ];
          this.totalKeys = res?.count || 0;
          this.loading = false;
        });
      }
    };
  }
}
